import { z } from 'zod';
import { ConditionGroupSchema } from '../conditions/condition.schemas';

export const sortQuestionsByOrder = (questions: Question[]) => {
  return [...questions].sort((a, b) => a.order - b.order);
};

const QuestionBaseSchema = z.object({
  id: z.string().uuid(),
  label: z.string().nonempty().max(200),
  order: z.number().int(),
  description: z.string().optional(),
  internalName: z.string().nonempty().max(50),
  internalDescription: z.string().nonempty().max(255),
  required: z.boolean(),
  displayConditions: ConditionGroupSchema.nullable().optional(),
});

const QuestionOptionSchema = z.object({
  label: z.string().nonempty(),
});

const QuestionTextParametersSchema = z.object({
  type: z.literal('text'),
  size: z.enum(['S', 'M', 'L']),
});

const QuestionRadioParametersSchema = z.object({
  type: z.literal('radio'),
  options: z.array(QuestionOptionSchema),
});

const QuestionSelectParametersSchema = z.object({
  type: z.literal('select'),
  options: z.array(QuestionOptionSchema),
  allowMultiple: z.boolean().default(false),
});

const QuestionCheckboxesParametersSchema = z.object({
  type: z.literal('checkboxes'),
  options: z.array(QuestionOptionSchema),
});

const QuestionDateParametersSchema = z.object({
  type: z.literal('date'),
});

const QuestionNumberParametersSchema = z.object({
  type: z.literal('number'),
  min: z.number().optional(),
  max: z.number().optional(),
  unit: z.string().optional(),
  decimalPlaces: z.number().min(0).optional(),
  defaultValue: z.number().optional(),
});

const QuestionDocumentParametersSchema = z.object({
  type: z.literal('document'),
  documentType: z.enum(['FILE', 'IMAGE', 'VIDEO']),
});

const QuestionVoiceParametersSchema = z.object({
  type: z.literal('voice'),
  maxDurationSeconds: z.number().int().min(1).max(600).default(120),
});

const QuestionParametersSchema = z
  .discriminatedUnion('type', [
    QuestionTextParametersSchema,
    QuestionRadioParametersSchema,
    QuestionSelectParametersSchema,
    QuestionCheckboxesParametersSchema,
    QuestionDateParametersSchema,
    QuestionNumberParametersSchema,
    QuestionDocumentParametersSchema,
    QuestionVoiceParametersSchema,
  ])
  .superRefine((data, ctx) => {
    if (
      data.type === 'number' &&
      data.min &&
      data.max &&
      data.min >= data.max
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Valeur minimale supérieure à la valeur maximale',
        path: ['min'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Valeur maximale inférieure à la valeur minimale',
        path: ['max'],
      });
    }

    if (
      data.type === 'select' ||
      data.type === 'checkboxes' ||
      data.type === 'radio'
    ) {
      if (data.options.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La question doit avoir au moins une option',
          path: ['options'],
        });
      }

      const duplicates = getDuplicateIndices(
        data.options.map((option) => option.label),
      );

      duplicates.forEach((index) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Ce libellé est déjà utilisé`,
          path: ['options', index, 'label'],
        });
      });
    }
  });

function getDuplicateIndices(strings: string[]): number[] {
  const seen = new Set<string>();
  const indices: number[] = [];

  strings.forEach((string, index) => {
    const normalized = string.trim().toLowerCase();
    if (seen.has(normalized)) {
      indices.push(index);
    } else {
      seen.add(normalized);
    }
  });

  return indices;
}

export const QuestionSchema = QuestionBaseSchema.extend({
  parameters: QuestionParametersSchema,
});

// Question type schemas registry
const QuestionTypeSchemas = {
  text: QuestionBaseSchema.extend({
    parameters: QuestionTextParametersSchema,
  }),
  radio: QuestionBaseSchema.extend({
    parameters: QuestionRadioParametersSchema,
  }),
  select: QuestionBaseSchema.extend({
    parameters: QuestionSelectParametersSchema,
  }),
  checkboxes: QuestionBaseSchema.extend({
    parameters: QuestionCheckboxesParametersSchema,
  }),
  date: QuestionBaseSchema.extend({
    parameters: QuestionDateParametersSchema,
  }),
  number: QuestionBaseSchema.extend({
    parameters: QuestionNumberParametersSchema,
  }),
  document: QuestionBaseSchema.extend({
    parameters: QuestionDocumentParametersSchema,
  }),
  voice: QuestionBaseSchema.extend({
    parameters: QuestionVoiceParametersSchema,
  }),
} as const;

export const QuestionTextSchema = QuestionTypeSchemas.text;
export const QuestionRadioSchema = QuestionTypeSchemas.radio;
export const QuestionSelectSchema = QuestionTypeSchemas.select;
export const QuestionCheckboxesSchema = QuestionTypeSchemas.checkboxes;
export const QuestionDateSchema = QuestionTypeSchemas.date;
export const QuestionNumberSchema = QuestionTypeSchemas.number;
export const QuestionDocumentSchema = QuestionTypeSchemas.document;
export const QuestionVoiceSchema = QuestionTypeSchemas.voice;

export const CreateQuestionSchema = QuestionSchema;

export const UpdateQuestionSchema = QuestionSchema.partial();

export type Question = z.infer<typeof QuestionSchema>;
export type QuestionParameters = Question['parameters'];
export type CreateQuestion = z.infer<typeof CreateQuestionSchema>;
export type UpdateQuestion = z.infer<typeof UpdateQuestionSchema>;

export type QuestionType = Question['parameters']['type'];
export type QuestionOption = z.infer<typeof QuestionOptionSchema>;

export type QuestionText = z.infer<typeof QuestionTextSchema>;
export type QuestionRadio = z.infer<typeof QuestionRadioSchema>;
export type QuestionSelect = z.infer<typeof QuestionSelectSchema>;
export type QuestionCheckboxes = z.infer<typeof QuestionCheckboxesSchema>;
export type QuestionDate = z.infer<typeof QuestionDateSchema>;
export type QuestionNumber = z.infer<typeof QuestionNumberSchema>;
export type QuestionDocument = z.infer<typeof QuestionDocumentSchema>;
export type QuestionVoice = z.infer<typeof QuestionVoiceSchema>;
